import omit from 'lodash/omit';
import type { RouteObject } from 'react-router';
import type {
  HasAccessFunction,
  MenuLink,
  NavCategory,
  NonMenuLink,
  RootGroup,
  RootLink,
} from '../@types/nav-object';
import { checkHasAccess } from './check-route-access';

function getRoutesBasedOnPermissions({
  routes,
  fullPathsWithAccess,
  parentPath,
  parentHasAccess,
  parentKey,
}: {
  routes: (RootLink | RootGroup | MenuLink | NonMenuLink)[];
  fullPathsWithAccess: Map<string, Set<string>>;
  parentPath: string;
  parentHasAccess?: HasAccessFunction;
  parentKey: string;
}): RouteObject[] {
  return routes.reduce<RouteObject[]>((acc, route, index) => {
    const key = `${parentKey}.${index}`;
    const path = route.index ? parentPath : `${parentPath}/${route.path}`;
    let { loader } = route;

    const matchingKeysForPathWithAccess = fullPathsWithAccess.get(path);

    if (
      !matchingKeysForPathWithAccess ||
      !matchingKeysForPathWithAccess.has(key)
    )
      return acc;

    if (route.hasAccess !== undefined || parentHasAccess !== undefined) {
      const { hasAccess } = route;

      loader = async (...args) => {
        if (parentHasAccess) {
          await checkHasAccess(parentHasAccess);
        }

        if (hasAccess) {
          await checkHasAccess(hasAccess);
        }

        if (route?.loader && typeof route.loader === 'function') {
          return route.loader(...args);
        }

        return null;
      };
    }
    const children = Array.isArray(route.children)
      ? getRoutesBasedOnPermissions({
          routes: route.children,
          fullPathsWithAccess,
          parentPath: path,
          parentKey: key,
        })
      : undefined;

    const routeWithoutCustomProps = omit(route, [
      'type',
      'title',
      'icon',
      'hasAccess',
    ]);
    const routeObject = {
      ...routeWithoutCustomProps,
      loader,
      children,
    } as RouteObject;

    acc.push(routeObject);

    return acc;
  }, []);
}

export function buildRouteTree(
  routes: NavCategory[],
  fullPathsWithAccess: Map<string, Set<string>>,
): RouteObject[] {
  return routes.reduce<RouteObject[]>((acc, route, index) => {
    if (Array.isArray(route.children)) {
      acc.push(
        ...getRoutesBasedOnPermissions({
          routes: route.children,
          fullPathsWithAccess,
          parentPath: '',
          parentHasAccess: route.hasAccess,
          parentKey: `${index}`,
        }),
      );
    }

    return acc;
  }, []);
}
